import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, A2UISurface } from '../components/ChatComponents';
import {
  agentApi,
  ChatHttpError,
  type ChatRequest,
  type SSELogEntry,
  type ToolCallInfo,
} from '../api';
import { settingsApi } from '../api/settings';
import { dispatchByErrorCode, type ErrorFlags } from './errorDispatch';
import { safeRandomUUID } from '../utils/uuid';

type Message = ChatMessage;

/** Debounce window — skip the spinner for short tool calls (~300ms). */
const GENERATING_DEBOUNCE_MS = 300;

/** Thought event step+content pair. Extracted so UIs can show `{step}` as a badge. */
export interface ThoughtStepInfo {
  step: string;
  content: string;
}

interface UseChatStreamParams {
  /**
   * Error reporter — called with the user-facing message plus optional UX flags
   * that ask the UI to block further sends, surface a Retry-After hint,
   * cancel edit mode, or clear pending attachments (see {@link ErrorFlags}).
   */
  onError: (msg: string, flags?: ErrorFlags) => void;
}

export function useChatStream({ onError }: UseChatStreamParams) {
  // -------- Core chat state --------
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPendingResponse, setIsPendingResponse] = useState(false);
  const pendingRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [promptLabel, setPromptLabel] = useState<string>('production');
  const [promptLabels, setPromptLabels] = useState<string[]>(['production', 'latest']);
  const [thoughtStep, setThoughtStep] = useState<ThoughtStepInfo | null>(null);

  // -------- Tool call state --------
  const [toolCalls, setToolCalls] = useState<ToolCallInfo[]>([]);
  const [toolPanelExpanded, setToolPanelExpanded] = useState(true);

  // Pending timers for (name → setTimeout id). The spinner only renders once
  // the timer fires; if completion arrives first, the timer is cleared and the
  // tool goes straight to 'calling'. Avoids flicker for <300ms tool calls.
  const generatingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // -------- SSE log state (optional — driven by onRawLine callback) --------
  const [sseLog, setSSELog] = useState<SSELogEntry[]>([]);

  // -------- Abort / stream cancellation --------
  const abortControllerRef = useRef<AbortController | null>(null);

  // -------- Server version --------
  const [serverVersion, setServerVersion] = useState('0.0.0');

  // Latest assistant message with A2UI surface — only that one is editable.
  const lastToolUiMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].toolUi) return messages[i].id;
    }
    return null;
  }, [messages]);

  const loadPromptLabels = () => {
    settingsApi
      .getPromptLabels()
      .then((labels) => {
        if (labels.length > 0) setPromptLabels(labels);
      })
      .catch(() => {});
  };

  const loadServerVersion = () => {
    agentApi.getServerVersion().then(setServerVersion);
  };

  /** Cancel every pending generating→calling timer + clear the map.
   * Common entry point for error paths, done, abort, unmount.
   */
  const clearAllGeneratingTimers = useCallback(() => {
    for (const timer of generatingTimersRef.current.values()) {
      clearTimeout(timer);
    }
    generatingTimersRef.current.clear();
  }, []);

  /** Phase 1 — `status: "generating"` arrived. Schedule a spinner after debounce.
   * Short tool calls that complete within {@link GENERATING_DEBOUNCE_MS}
   * skip the intermediate state entirely (no flicker).
   */
  const handleToolCallGenerating = useCallback(
    (toolName: string, aiMsgId: string) => {
      // Don't re-schedule if we've already got a pending timer for this tool
      // in the current message. (The server dedupes, but defend against re-sends.)
      if (generatingTimersRef.current.has(toolName)) return;

      const timer = setTimeout(() => {
        generatingTimersRef.current.delete(toolName);
        const toolId = `tool-${toolName}-${safeRandomUUID()}`;
        setToolCalls((prev) => {
          // If completion already fired (e.g. super-fast tool that landed
          // before the debounce expired), don't add a phantom generating row.
          if (prev.some((tc) => tc.messageId === aiMsgId && tc.name === toolName)) {
            return prev;
          }
          return [
            ...prev,
            {
              id: toolId,
              messageId: aiMsgId,
              name: toolName,
              arguments: {},
              status: 'generating',
              startTime: Date.now(),
            },
          ];
        });
        setToolPanelExpanded(true);
      }, GENERATING_DEBOUNCE_MS);

      generatingTimersRef.current.set(toolName, timer);
    },
    [],
  );

  /** Phase 2 — `input` arrived. Upgrade an existing generating row or insert
   * a fresh 'calling' row. Also clears any pending debounce for this tool.
   */
  const handleToolCallCompletion = useCallback(
    (toolName: string, input: Record<string, unknown>, aiMsgId: string, toolArgsBuffer: Record<string, string>) => {
      // Cancel the scheduled spinner — we're past that phase now.
      const pendingTimer = generatingTimersRef.current.get(toolName);
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        generatingTimersRef.current.delete(toolName);
      }

      setToolCalls((prev) => {
        // Upgrade existing generating row → calling (same tool+message).
        const existing = prev.find(
          (tc) => tc.messageId === aiMsgId && tc.name === toolName && tc.status === 'generating',
        );
        if (existing) {
          toolArgsBuffer[toolName] = existing.id;
          return prev.map((tc) =>
            tc.id === existing.id
              ? { ...tc, arguments: input, status: 'calling' as const }
              : tc,
          );
        }
        // No prior generating row — either debounce skipped or provider didn't
        // stream chunks. Insert directly as 'calling'.
        const toolId = `tool-${toolName}-${safeRandomUUID()}`;
        toolArgsBuffer[toolName] = toolId;
        return [
          ...prev,
          {
            id: toolId,
            messageId: aiMsgId,
            name: toolName,
            arguments: input,
            status: 'calling',
            startTime: Date.now(),
          },
        ];
      });
      setToolPanelExpanded(true);
    },
    [],
  );

  /** Cancel the in-flight stream (if any) and clear debounce timers.
   * Safe to call even if no stream is active.
   */
  const handleAbort = useCallback(() => {
    abortControllerRef.current?.abort();
    clearAllGeneratingTimers();
    setIsStreaming(false);
  }, [clearAllGeneratingTimers]);

  // Shared SSE stream processor (used by handleSend & handleEditResend).
  // Owns the AbortController for the duration of the stream.
  const processStreamEvents = async (chatRequest: ChatRequest, aiMsgId: string) => {
    const toolArgsBuffer: Record<string, string> = {};
    let fullContent = '';

    // Fresh controller per send. Stored on ref so handleAbort can reach it.
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Each new stream starts with an empty SSE log (so the panel reflects
    // only the current turn). Caller sets isStreaming; we handle the rest.
    setSSELog([]);

    const onRawLine = (entry: SSELogEntry) => {
      setSSELog((prev) => [...prev, entry]);
    };

    for await (const event of agentApi.chatStream(chatRequest, signal, onRawLine)) {
      const eventType = event.event;

      try {
        const parsed = JSON.parse(event.data);

        switch (eventType) {
          case 'thought': {
            setThoughtStep({
              step: parsed.step || 'thinking',
              content: parsed.content || '',
            });
            break;
          }

          case 'answer': {
            setThoughtStep(null);
            const token = parsed.token || parsed.delta || '';
            if (token) {
              fullContent += token;
              setMessages((prev) => prev.map((msg) => (msg.id === aiMsgId ? { ...msg, content: fullContent } : msg)));
            }
            break;
          }

          case 'tool_call': {
            setThoughtStep(null);
            const toolName: string = parsed.tool || 'unknown';
            if (parsed.status === 'generating') {
              handleToolCallGenerating(toolName, aiMsgId);
            } else {
              handleToolCallCompletion(toolName, parsed.input || {}, aiMsgId, toolArgsBuffer);
            }
            break;
          }

          case 'tool_result': {
            const resultToolName = parsed.tool || '';
            const matchId = toolArgsBuffer[resultToolName];
            if (matchId) {
              setToolCalls((prev) =>
                prev.map((tc) =>
                  tc.id === matchId
                    ? {
                        ...tc,
                        status: 'completed',
                        result: parsed.output || JSON.stringify(parsed),
                        endTime: Date.now(),
                      }
                    : tc,
                ),
              );
            }

            // Capture a2ui from Pydantic-validated tool_result
            if (resultToolName === 'a2ui' && parsed.output) {
              try {
                const surface: A2UISurface = JSON.parse(parsed.output);
                setMessages((prev) => prev.map((msg) => (msg.id === aiMsgId ? { ...msg, toolUi: surface } : msg)));
              } catch {
                // Ignore parse errors
              }
            }
            break;
          }

          case 'error': {
            // In-stream error — dispatch through the same code map as pre-stream.
            clearAllGeneratingTimers();
            const errorDetail = parsed.detail || parsed.message || '스트리밍 오류가 발생했습니다.';
            const errorCode = parsed.error_code || 'STREAM_ERROR';
            dispatchByErrorCode(onError, errorCode, errorDetail, parsed);
            break;
          }

          case 'meta': {
            if (parsed.cache_hit_rate !== undefined) {
              const metaData = {
                input_tokens: parsed.input_tokens ?? 0,
                output_tokens: parsed.output_tokens ?? 0,
                cache_read_tokens: parsed.cache_read_tokens ?? 0,
                cache_hit_rate: parsed.cache_hit_rate ?? 0,
                generating_emitted: parsed.generating_emitted ?? 0,
                // Read label from the *request* (not state) — user may have
                // toggled the dropdown mid-stream, and we want to label the
                // message with whatever was sent, not the current UI state.
                prompt_label: chatRequest.prompt_label ?? 'production',
              };
              setMessages((prev) =>
                prev.map((msg) => (msg.id === aiMsgId ? { ...msg, metadata: metaData } : msg)),
              );
            }
            break;
          }

          case 'done':
            clearAllGeneratingTimers();
            break;

          default:
            if (parsed.delta) {
              fullContent += parsed.delta;
              setMessages((prev) => prev.map((msg) => (msg.id === aiMsgId ? { ...msg, content: fullContent } : msg)));
            } else if (parsed.content && typeof parsed.content === 'string') {
              fullContent += parsed.content;
              setMessages((prev) => prev.map((msg) => (msg.id === aiMsgId ? { ...msg, content: fullContent } : msg)));
            }
        }
      } catch {
        if (event.data && !event.data.startsWith('{')) {
          fullContent += event.data;
          setMessages((prev) => prev.map((msg) => (msg.id === aiMsgId ? { ...msg, content: fullContent } : msg)));
        }
      }
    }
  };

  /** Thin wrapper around `processStreamEvents` that unifies pre-stream errors
   * (thrown synchronously before the generator yields) with in-stream errors
   * through the same {@link dispatchByErrorCode} map. Callers use this when
   * they want the hook to own failure UX; otherwise call processStreamEvents
   * directly and handle errors yourself.
   */
  const sendWithErrorHandling = useCallback(
    async (chatRequest: ChatRequest, aiMsgId: string) => {
      setIsStreaming(true);
      try {
        await processStreamEvents(chatRequest, aiMsgId);
      } catch (err) {
        clearAllGeneratingTimers();
        if (err instanceof ChatHttpError) {
          dispatchByErrorCode(onError, err.errorCode, err.detail, {
            retryAfter: err.retryAfter,
            ...err.extra,
          });
        } else if (err instanceof DOMException && err.name === 'AbortError') {
          // User-initiated abort — not an error condition; no onError call.
        } else {
          onError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setIsStreaming(false);
      }
    },
    // processStreamEvents is re-created on every render; deliberately excluded
    // from deps — it closes over stable setters/refs only. Listing it would
    // cause every render to invalidate the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onError, clearAllGeneratingTimers],
  );

  // Cleanup on unmount — cancel any in-flight stream and pending timers.
  useEffect(() => {
    return () => {
      // Inline (Option A per spec R15-Minor 2) — avoids exhaustive-deps warning
      // by capturing refs directly rather than calling external callbacks.
      abortControllerRef.current?.abort();
      for (const timer of generatingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      // No need to clear the Map on unmount — ref itself will be GC'd.
    };
  }, []);

  return {
    // State
    messages,
    setMessages,
    input,
    setInput,
    isStreaming,
    setIsStreaming,
    isPendingResponse,
    setIsPendingResponse,
    pendingRetryRef,
    promptLabel,
    setPromptLabel,
    promptLabels,
    thoughtStep,
    setThoughtStep,
    toolCalls,
    setToolCalls,
    toolPanelExpanded,
    setToolPanelExpanded,
    sseLog,
    setSSELog,
    serverVersion,
    lastToolUiMsgId,
    // Functions
    loadPromptLabels,
    loadServerVersion,
    processStreamEvents,
    sendWithErrorHandling,
    handleAbort,
  };
}
